import { useState, useEffect, useRef, useMemo } from 'react'
import { clickable } from '../lib/a11y'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { railBusy, claimRail, releaseRail, subscribeRail } from '../lib/toastRail'
import { capture } from '../lib/batchReceipt'
// isOverlayBusy/CELEBRATION_RAIL_ID moved to lib/overlayBusy.js (2026-08-15) so
// eager consumers (ActivityToast) don't drag this whole lazy-loaded component
// in just to read three window flags. Re-exported here so nothing else that
// imports from this file needs to change.
export { isOverlayBusy, CELEBRATION_RAIL_ID } from '../lib/overlayBusy'
import { isOverlayBusy, CELEBRATION_RAIL_ID } from '../lib/overlayBusy'

// ── Celebration toast — slides up from bottom with confetti burst ───────────
// Listens for window events:
//   nextep:card-minted   detail: card object
//   nextep:milestone        detail: { title, body, icon, route, share? }
//     share (optional): { url, text } — renders a 📣 Share button on the toast that
//     fires the native share sheet (clipboard fallback), so a peak moment (a race
//     win, a big milestone) can be broadcast in one tap without a separate surface.
// Multiple events QUEUE and play one at a time so none get stomped by the next.
// Each shows ~4.2s. Tap to navigate; X to dismiss early.

export function CelebrationListener() {
  const navigate = useNavigate()
  const [queue, setQueue] = useState([])
  const [, setGate] = useState(0) // bump (re-render) to re-check the overlay hold
  const idRef = useRef(0)

  useEffect(() => {
    const enqueue = (it) => setQueue(q => {
      // Coalesce a backlog into ONE summary so a burst (e.g. many milestones at
      // once) can't become a minute-long train of toasts.
      const MAX = 5
      if (q.length >= MAX) {
        const last = q[q.length - 1]
        const count = (last?.kind === 'summary' ? last.count : 0) + 1
        const summary = { kind: 'summary', title: 'More Unlocked', body: `+${count} update${count > 1 ? 's' : ''}`, icon: '✨', route: undefined, count, _id: last?.kind === 'summary' ? last._id : ++idRef.current }
        return last?.kind === 'summary' ? [...q.slice(0, -1), summary] : [...q, summary]
      }
      return [...q, { ...it, _id: ++idRef.current }]
    })
    // card-minted is handled by CardReveal's full-screen cinematic, and
    // achievement-unlocked by AchievementUnlockListener's full-screen medallion
    // moment (src/components/AchievementUnlock.jsx) — a toast here too was a
    // redundant second surface for the same event (Jason 2026-06-29, extended
    // to achievements 2026-08-05 when the medallion moment replaced its toast).
    const onMilestone = (e) => {
      const m = e.detail
      if (!m?.title) return
      if (capture('milestone', e)) return   // a cascade's receipt lists it instead
      enqueue({ kind: 'milestone', title: m.title, body: m.body || '', icon: m.icon || '✨', route: m.route, share: m.share, accentRgb: m.accentRgb, confettiEmojis: m.confettiEmojis, payload: m })
    }
    // When an overlay closes it dispatches wrap-done/card-done — re-check if we can present.
    const bump = () => setGate(g => g + 1)
    window.addEventListener('nextep:milestone', onMilestone)
    window.addEventListener('nextep:wrap-done', bump)
    window.addEventListener('nextep:card-done', bump)
    return () => {
      window.removeEventListener('nextep:milestone', onMilestone)
      window.removeEventListener('nextep:wrap-done', bump)
      window.removeEventListener('nextep:card-done', bump)
    }
  }, [])

  const item = queue[0] || null
  const [leaving, setLeaving] = useState(false)
  const [shared, setShared] = useState(false)
  const leavingRef = useRef(false)
  useEffect(() => { setShared(false) }, [item?._id])

  const doShare = async (e) => {
    e.stopPropagation()
    const s = item?.share
    if (!s?.url) return
    try {
      if (navigator.share) await navigator.share({ title: 'NextEp', text: s.text || '', url: s.url })
      else { await navigator.clipboard.writeText(`${s.text ? s.text + ' ' : ''}${s.url}`); setShared(true); setTimeout(() => setShared(false), 1600) }
    } catch (_) { /* user cancelled */ }
  }

  // Hold the toast while a full-screen overlay owns the screen (Jason 2026-07-06:
  // an Achievement Unlocked toast was landing on top of the "Updated!" sheet). We
  // keep the item queued and re-check on overlay-close events + a poll fallback.
  // Also wait for the bottom rail: Celebration sits at bottom:96px, within 24px of the
  // undo / rating / pulse toasts, so without this they simply overlapped. It queues
  // internally, so waiting costs nothing — the moment holds nothing is dropped.
  const held = !!item && (isOverlayBusy() || railBusy(CELEBRATION_RAIL_ID))
  useEffect(() => {
    if (!held) return
    const iv = setInterval(() => setGate(g => g + 1), 450)
    return () => clearInterval(iv)
  }, [held])

  // Animate OUT before removing, so the toast slides/fades away instead of
  // vanishing (Jason 2026-07-03). Ref-guarded so auto-timeout + tap can't
  // double-advance the queue.
  const dismiss = () => {
    if (leavingRef.current) return
    leavingRef.current = true
    setLeaving(true)
    setTimeout(() => { leavingRef.current = false; setLeaving(false); setQueue(q => q.slice(1)) }, 340)
  }

  // Hold each item ~4.2s, then animate it out and advance. Does not start while a
  // full-screen overlay is up (held) — the toast waits its turn.
  useEffect(() => {
    if (!item || held) return
    const t = setTimeout(dismiss, 4200)
    return () => clearTimeout(t)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, held])

  // Own the bottom rail while a toast is actually on screen; release unconditionally on
  // unmount so a stuck holder can never silence the rail for everyone else.
  useEffect(() => {
    if (item && !held) claimRail(CELEBRATION_RAIL_ID); else releaseRail(CELEBRATION_RAIL_ID)
    return () => releaseRail(CELEBRATION_RAIL_ID)
  }, [item, held])
  useEffect(() => subscribeRail(() => setGate(g => g + 1)), [])

  if (!item || held) return null

  const accentRgb = item.accentRgb || 'var(--amber-rgb)'
  const accentVar = item.accentRgb ? `rgba(${accentRgb},1)` : 'var(--amber)'

  return createPortal((
    <div style={{
      position: 'fixed',
      bottom: 'calc(var(--nav-h) + 12px)',
      left: 12, right: 12,
      zIndex: 'var(--z-toast)',
      display: 'flex', justifyContent: 'center',
      pointerEvents: 'none',
      fontFamily: 'var(--font)',
    }}>
      {/* v17: no confetti. One quiet pill, the show's accent on the icon disc only. */}

      <div
        key={item._id}
        {...clickable(() => {
          if (item.route) navigate(item.route)
          dismiss()
        })}
        style={{
          maxWidth: 420, width: '100%',
          background: 'var(--bg-raised)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-pill)',
          padding: '10px 14px 10px 12px',
          display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
          cursor: 'pointer',
          pointerEvents: 'auto',
          '--nxa': leaving
            ? 'celebrationSlideDown 0.34s cubic-bezier(0.4, 0, 1, 1) forwards'
            : 'celebrationSlideUp 0.5s cubic-bezier(0.34, 1.4, 0.64, 1)',
          position: 'relative',
        }}
      >
        {/* Icon disc, pulses gold on entry — soft glow ring behind it (borrowed
            polish from the design-tool recreation's full-screen glow ring,
            scaled down to fit the toast; the toast form factor itself stays,
            see isOverlayBusy()/rail-busy comments above for why). */}
        <div style={{ position: 'relative', flexShrink: 0, width: 'var(--toast-chip-size)', height: 'var(--toast-chip-size)' }}>
          <div style={{
            position: 'relative', width: '100%', height: '100%', borderRadius:'var(--radius-full)',
            background: `linear-gradient(135deg, rgba(${accentRgb},0.25), rgba(${accentRgb},0.08))`,
            border: `1px solid rgba(${accentRgb},0.4)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 'var(--toast-chip-icon-size)',
            '--nxa': 'celebrationIconPulse 0.6s cubic-bezier(0.34,1.2,0.64,1)',
          }}>{item.icon}</div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            color: accentVar, fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 500,
            letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 2,
          }}>{item.title}</div>
          <div style={{
            color: 'var(--text)', fontWeight: 500, fontSize: 14, lineHeight: 1.2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{item.body}</div>
        </div>

        {/* queue count, lets the user know more are coming */}
        {queue.length > 1 && (
          <div style={{
            flexShrink: 0, fontSize: 10, fontWeight: 700, color: accentVar,
            background: `rgba(${accentRgb},0.12)`, borderRadius:'var(--radius-sm)', padding: '2px 7px',
          }}>+{queue.length - 1}</div>
        )}

        {item.share?.url && (
          <button
            onClick={doShare}
            aria-label="Share"
            style={{
              flexShrink: 0, background: `rgba(${accentRgb},0.16)`,
              border: `1px solid rgba(${accentRgb},0.4)`, borderRadius: 'var(--radius-pill)',
              color: accentVar, cursor: 'pointer', fontSize: 12, fontWeight: 700,
              padding: '6px 12px', fontFamily: 'var(--font)', whiteSpace: 'nowrap',
            }}
          >{shared ? '✓ Copied' : '📣 Share'}</button>
        )}

        <button
          onClick={(e) => { e.stopPropagation(); dismiss() }}
          aria-label="Close"
          style={{
            flexShrink: 0, background: 'none', border: 'none',
            color: 'var(--text-dim)', cursor: 'pointer', fontSize: 18,
            padding: '4px 8px',
          }}
        >×</button>
      </div>

      <style>{`
        @keyframes celebrationSlideUp {
          from { opacity: 0; transform: translateY(40px) scale(0.94); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
        @keyframes celebrationSlideDown {
          from { opacity: 1; transform: translateY(0)   scale(1); }
          to   { opacity: 0; transform: translateY(46px) scale(0.94); }
        }
        @keyframes celebrationIconPulse {
          0%   { transform: scale(0.7); }
          60%  { transform: scale(1.18); }
          100% { transform: scale(1); }
        }
        @keyframes celebrationGlowPulse {
          0%, 100% { opacity: 0.5; }
          50%      { opacity: 1; }
        }
        @keyframes confettiFall {
          0%   { opacity: 0; transform: translate(0, -10px) rotate(0deg); }
          15%  { opacity: 1; }
          100% { opacity: 0; transform: translate(var(--cx), 80px) rotate(var(--cr)); }
        }
      `}</style>
    </div>
  ), document.body)
}
